import React from 'react';
import {
    Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IReviewSummary } from '../../interfaces/appraisal.interface';
import { displayUsername } from '../../utils/userUtils';
import { bandColor, formatScore, isNotCompleted, reviewStatusColor } from './bands';
import { formatDate, isManagerOverdue, isSelfOverdue } from './dates';

/** Whose list this is: the employee's own, the reviewer's queue, or the admin's cycle overview. */
export type ReviewTableMode = 'mine' | 'toWrite' | 'admin';

interface Props {
    reviews: IReviewSummary[];
    loading: boolean;
    mode: ReviewTableMode;
    emptyText: string;
}

export const StatusChips: React.FC<{ review: IReviewSummary }> = ({ review }) => {
    const { t } = useTranslation('appraisal');
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={t(`reviewStatus.${review.status}`)} color={reviewStatusColor[review.status]} />
            {isNotCompleted(review) && <Chip size="small" variant="outlined" color="warning" label={t('notCompleted')} />}
        </Box>
    );
};

export const BandChip: React.FC<{ review: Pick<IReviewSummary, 'band'> }> = ({ review }) => {
    const { t } = useTranslation('appraisal');
    if (!review.band) return <>—</>;
    return <Chip size="small" label={t(`band.${review.band}`)} color={bandColor[review.band]} />;
};

const DueCell: React.FC<{ date: string; overdue: boolean }> = ({ date, overdue }) => {
    const { t } = useTranslation('appraisal');
    return (
        <Box component="span" sx={{ color: overdue ? 'warning.main' : undefined, fontWeight: overdue ? 600 : undefined }}>
            {formatDate(date)}
            {overdue && <Chip size="small" color="warning" label={t('overdue')} sx={{ ml: 1 }} />}
        </Box>
    );
};

const ReviewTable: React.FC<Props> = ({ reviews, loading, mode, emptyText }) => {
    const { t } = useTranslation('appraisal');
    const navigate = useNavigate();
    const showEmployee = mode !== 'mine';
    const showCycle = mode !== 'admin';
    const showReviewer = mode !== 'toWrite';
    const colSpan = 5 + (showEmployee ? 2 : 0) + (showCycle ? 1 : 0) + (showReviewer ? 1 : 0);

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {showEmployee && <TableCell>{t('columns.employee')}</TableCell>}
                        {showEmployee && <TableCell>{t('columns.department')}</TableCell>}
                        {showCycle && <TableCell>{t('columns.cycle')}</TableCell>}
                        {showReviewer && <TableCell>{t('columns.reviewer')}</TableCell>}
                        <TableCell>{t('columns.status')}</TableCell>
                        <TableCell>{mode === 'toWrite' ? t('columns.managerDue') : t('columns.selfDue')}</TableCell>
                        <TableCell>{t('columns.score')}</TableCell>
                        <TableCell>{t('columns.band')}</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow><TableCell colSpan={colSpan}><Typography>{t('loading')}</Typography></TableCell></TableRow>
                    ) : reviews.length === 0 ? (
                        <TableRow><TableCell colSpan={colSpan}><Typography color="text.secondary">{emptyText}</Typography></TableCell></TableRow>
                    ) : reviews.map(review => (
                        <TableRow key={review.id} hover>
                            {showEmployee && <TableCell>{displayUsername(review.employee.name)}</TableCell>}
                            {showEmployee && <TableCell>{review.employee.departmentNames.join(', ') || '—'}</TableCell>}
                            {showCycle && <TableCell>{review.cycleName}</TableCell>}
                            {showReviewer && (
                                <TableCell>
                                    {review.reviewer
                                        ? displayUsername(review.reviewer.name)
                                        : <Typography variant="body2" color="warning.main">{t('participants.noReviewer')}</Typography>}
                                </TableCell>
                            )}
                            <TableCell><StatusChips review={review} /></TableCell>
                            <TableCell>
                                {mode === 'toWrite'
                                    ? <DueCell date={review.managerReviewDue} overdue={isManagerOverdue(review)} />
                                    : <DueCell date={review.selfReviewDue} overdue={isSelfOverdue(review)} />}
                            </TableCell>
                            <TableCell>{formatScore(review.managerScore)}</TableCell>
                            <TableCell><BandChip review={review} /></TableCell>
                            <TableCell align="right">
                                <Button size="small" onClick={() => navigate(`/appraisals/reviews/${review.id}`)}>
                                    {t('open')}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ReviewTable;
